/**
 * registry.ts — cue identifier types + registry entry shapes (v2M8.1).
 *
 * The cue registry is the seam that AUDIO.md §2.4's procedural-vs-commissioned
 * decision turns on. Each `CueId` resolves to either:
 *   - a `ProceduralEntry` whose `build` function returns a freshly-wired
 *     Web Audio source ready to `start()` (used for v2M8 procedural cues), or
 *   - a `SampleEntry` carrying a decoded `AudioBuffer` (used for v3M11 polish
 *     when commissioned clips replace procedurals).
 *
 * A v3M11 swap is a one-line change in this file: re-register the cue with
 * `registerSample(...)` instead of `registerProcedural(...)`. Every caller
 * (UI hover, weapon fire, ship destruction, etc.) keeps passing the same
 * `cueId` to `engine.playCue`. The render layer never learns whether the
 * cue is synthesised or a sample.
 *
 * Bus routing per cue is stamped on the registry entry so the engine
 * doesn't have to inspect cue IDs to decide which gain node to wire to.
 */

/**
 * Cue identifier — literal union populated as cues land per sub-checkpoint.
 * v2M8.2 lands the four UI cues; subsequent sub-checkpoints extend by
 * appending new arms (alert tones in v2M8.3, weapon cues in v2M8.4, etc.).
 *
 * Naming convention: `{category}:{event}`. Keeps grep-friendly when audit
 * time comes around in v3M11 polish (every cue maps 1:1 to a registry
 * entry, and the prefix tells you which `cues-*.ts` file to look in).
 */
export type CueId =
  // v2M8.2 — UI SFX (AUDIO §10).
  | 'ui:click'
  | 'ui:hover'
  | 'ui:panel-open'
  | 'ui:panel-close'
  // v2M8.3 — alert tones (AUDIO §10 / UI_UX §1.5 categories).
  | 'alert:red'
  | 'alert:amber'
  | 'alert:blue'
  | 'alert:green'
  // v2M8.4 — weapon SFX per damage type (AUDIO §6). Each damage type
  // gets fire + impact-on-shield + impact-on-hull. Concussion's hull
  // variant is acoustically identical to the shield variant in v1
  // (splash deferred per MECHANICS); EMP's hull variant is a one-shot
  // for v2M8.4, with the looping-for-disable-duration version flagged
  // as a v3M11 polish item (sustained cues need extra infra).
  | 'weapon:energy:fire'
  | 'weapon:energy:shield'
  | 'weapon:energy:hull'
  | 'weapon:kinetic:fire'
  | 'weapon:kinetic:shield'
  | 'weapon:kinetic:hull'
  | 'weapon:concussion:fire'
  | 'weapon:concussion:shield'
  | 'weapon:concussion:hull'
  | 'weapon:emp:fire'
  | 'weapon:emp:shield'
  | 'weapon:emp:hull'
  // v3M28.5 — per-family missile cues. Launch fires at the launcher's
  // position the tick a `Missile` entity appears in `state.projectiles`;
  // impact fires at the target's position when a `willHit === true`
  // missile despawns at arrival. Misses + target-gone despawns are
  // silent per MISSILES.md §7. Procedural variants in v3M28.5; sample-
  // based ElevenLabs assets swap in at v3M28.6 with the same ids.
  | 'weapon:missile:launch:pulson'
  | 'weapon:missile:launch:zeon'
  | 'weapon:missile:impact:pulson'
  | 'weapon:missile:impact:zeon'
  // v4M9.2.4 — Torpedo cues. Launch fires at the launcher position the
  // tick a `Projectile` with `family === 'Torpedo'` appears in
  // `state.projectiles`; impact fires at the target position on a
  // `willHit` despawn. Procedural for now (matches the v3M28.5 Missile
  // baseline); commissioned ElevenLabs samples swap in later via
  // `registerSample`. No sub-family suffix because Torpedo doesn't have
  // a Pulson/Zeon-style internal split — per-Mk timbre variation can
  // ride on `hashVariation` like every other procedural cue.
  | 'weapon:torpedo:launch'
  | 'weapon:torpedo:impact'
  // v2M8.5 — ship lifecycle (AUDIO §7 + §8). Six single-shot cues
  // paired 1:1 with their VFX counterparts in v2M7.5 / v2M7.6.
  | 'ship:destroyed'
  | 'ship:hyperspace-out'
  | 'ship:hyperspace-in'
  | 'ship:cloak-engage'
  | 'ship:cloak-disengage'
  | 'ship:throttle-up'
  // v2M8.6 — structures (AUDIO §8). Three single-shot cues; ambient
  // loops (Extractor / Repair Yard) defer to v3M11 polish.
  | 'structure:construction-start'
  | 'structure:complete'
  | 'structure:destroyed'
  // MFix3.6 — colonisation lifecycle (PLANETS §2.2). Single-shot cue
  // when a Coloniser successfully lands on an uninhabited planet.
  | 'colony:established'
  // v3M12.3 — end-game music stingers (AUDIO §5.1). Other beds
  // (menu, ambient, combat overlay) defer to v3M11 polish.
  | 'music:victory'
  | 'music:defeat';

/**
 * Per-call variation parameters — drives the AUDIO §6.1 deterministic
 * randomisation that prevents machine-gun-same-sample fatigue on
 * weapon SFX. Engine fills these from `PlayCueOpts.pitchMul` /
 * `gainMul` (defaulting both to 1.0) and passes them to the procedural
 * builder. Cues that don't care (UI / alerts) ignore the third arg.
 */
export interface CueVariation {
  pitchMul: number;
  gainMul: number;
}

/** Which bus a cue routes to. Maps to `AudioEngine`'s 4 gain nodes. */
export type CueBus = 'music' | 'sfx' | 'ui';

/**
 * Procedural entry — `build` is invoked each time the cue plays and must
 * return a started-or-ready-to-start `AudioScheduledSourceNode` (oscillator,
 * buffer source, etc.) connected to a `target` node. The engine wires the
 * `target` into the bus chain (panning + attenuation gain + bus). Builders
 * own their internal graph (filters, envelopes) and must connect the final
 * stage to `target`.
 */
export interface ProceduralEntry {
  kind: 'procedural';
  bus: CueBus;
  build: (
    ctx: AudioContext,
    target: AudioNode,
    variation?: CueVariation,
  ) => AudioScheduledSourceNode;
}

/**
 * Sample entry — points to a decoded AudioBuffer. v3M11 polish registers
 * these with the buffers loaded from `assets/audio/`.
 */
export interface SampleEntry {
  kind: 'sample';
  bus: CueBus;
  buffer: AudioBuffer;
}

export type RegistryEntry = ProceduralEntry | SampleEntry;

/** Mutable cue registry. The engine reads from this when `playCue` is called. */
export class CueRegistry {
  private readonly entries = new Map<CueId, RegistryEntry>();

  /**
   * Register a procedural builder. v2M8 uses this for every cue that
   * synthesis flatters (UI, weapons, destruction, hyperspace, alerts, etc.).
   */
  registerProcedural(
    cueId: CueId,
    bus: CueBus,
    build: ProceduralEntry['build'],
  ): void {
    this.entries.set(cueId, { kind: 'procedural', bus, build });
  }

  /**
   * Register a sample-backed cue. v3M11 polish replaces procedural builders
   * with this — same `cueId`, but the buffer comes from a commissioned
   * asset loaded at app boot.
   */
  registerSample(cueId: CueId, bus: CueBus, buffer: AudioBuffer): void {
    this.entries.set(cueId, { kind: 'sample', bus, buffer });
  }

  get(cueId: CueId): RegistryEntry | undefined {
    return this.entries.get(cueId);
  }

  has(cueId: CueId): boolean {
    return this.entries.has(cueId);
  }
}
